<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { trpc } from '../trpc';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';

interface Lesson {
  id: string;
  title: string;
  order: number;
  skipped: boolean;
}

interface Curriculum {
  id: string;
  title: string;
}

interface Card {
  id: string;
  shortId: string;
  lessonId: string | null;
  question: string;
  answer: string;
  goal: string | null;
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const curriculum = ref<Curriculum | null>(null);
const lessonList = ref<Lesson[]>([]);
const cardList = ref<Card[]>([]);
const loading = ref(false);
const error = ref('');

interface LessonGroup {
  lesson: Lesson;
  cards: Card[];
}

const grouped = computed<LessonGroup[]>(() => {
  return lessonList.value
    .map((lesson) => ({
      lesson,
      cards: cardList.value.filter((c) => c.lessonId === lesson.id),
    }))
    .filter((g) => g.cards.length > 0);
});

const totalCards = computed(() => cardList.value.length);

async function fetchData() {
  if (!user.value) return;
  const id = route.params.id as string;
  loading.value = true;
  error.value = '';

  try {
    const [curr, lessons, cards] = await Promise.all([
      trpc.getCurriculum.query({ id }),
      trpc.listLessons.query({ curriculumId: id }),
      trpc.listFlashcards.query({ curriculumId: id, limit: 500 }) as Promise<Card[]>,
    ]);

    curriculum.value = curr;
    lessonList.value = lessons.sort(
      (a: { order: number }, b: { order: number }) => a.order - b.order
    );
    cardList.value = cards;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load items';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }
  fetchData();
});

watch(user, (newUser) => {
  if (newUser) fetchData();
});

watch(
  () => route.params.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) fetchData();
  }
);
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>
    <div class="flex-1 overflow-y-auto scrollbar-thin">
      <div class="max-w-4xl mx-auto px-6 py-8">
        <div
          v-if="isLoading || loading"
          class="text-center text-muted-foreground"
        >
          Loading...
        </div>

        <div
          v-else-if="error"
          class="space-y-4"
        >
          <div class="bg-destructive/10 text-destructive p-4 rounded-md">
            {{ error }}
          </div>
          <Button
            variant="ghost"
            @click="router.push('/learn')"
          >
            Back to Curricula
          </Button>
        </div>

        <div
          v-else-if="curriculum"
          class="space-y-6"
        >
          <div>
            <button
              class="text-sm text-muted-foreground hover:text-foreground mb-2"
              @click="router.push(`/learn/${curriculum.id}`)"
            >
              &larr; Back to {{ curriculum.title }}
            </button>
            <h1 class="text-3xl font-bold">
              Review Cards
            </h1>
            <p class="text-muted-foreground mt-2 text-sm">
              {{ totalCards }} card{{ totalCards === 1 ? '' : 's' }}
            </p>
          </div>

          <div
            v-if="grouped.length === 0"
            class="bg-card rounded-lg border border-border p-8 text-center text-muted-foreground"
          >
            No cards yet for this curriculum.
          </div>

          <section
            v-for="group in grouped"
            :key="group.lesson.id"
            class="space-y-4"
          >
            <h2
              class="text-xl font-semibold border-b border-border pb-2"
              :class="group.lesson.skipped ? 'line-through opacity-60' : ''"
            >
              {{ group.lesson.order + 1 }}. {{ group.lesson.title }}
            </h2>

            <div class="space-y-2">
              <div
                v-for="card in group.cards"
                :key="card.id"
                class="bg-card rounded-md border border-border p-3 text-sm space-y-1"
              >
                <div
                  v-if="card.goal"
                  class="text-xs uppercase tracking-wide text-muted-foreground"
                >
                  Goal: <span class="normal-case tracking-normal italic text-foreground">{{ card.goal }}</span>
                </div>
                <div class="font-medium">
                  {{ card.question }}
                </div>
                <div class="text-muted-foreground whitespace-pre-wrap">
                  {{ card.answer }}
                </div>
                <div class="text-[10px] uppercase tracking-wide text-muted-foreground/60 pt-1 font-mono">
                  {{ card.shortId }}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
